import React, { useState, useEffect } from 'react';
import theme from './DataTooltip.module.scss';
import { getResidentialUnitExtraData } from '@/services/residential-units';
import { Tooltip, TooltipChildren } from '@/components/Tooltip';
import { ResidentialUnitExtraData } from '@/types';
import { Trans, Plural } from '@lingui/react/macro';
import { UserIcon } from '@/components/Icons';
import TooltipLoading from '@/components/Tooltip/TooltipLoading';
import { ProjectRelativeLink } from '@/components/navigation';
import { DateDistanceDisplay } from '@/components/localization';
import { ResidentialUnitDetailsTab } from '../residential-units/ResidentialUnitDetails/ResidentialUnitDetails';
import { MailToLink, TelLink } from '@ygtechnologies/bygr-frontend-component-library';

const Content: React.FC<{ data: ResidentialUnitExtraData }> = ({ data }) => {
    return (
        <div className={theme.container}>
            <div className={theme.section}>
                {data.floor && (
                    <div>
                        <Trans>Floor: {data.floor}</Trans>
                    </div>
                )}
                {data.layoutType && (
                    <div>
                        <Trans>Layout: {data.layoutType}</Trans>
                    </div>
                )}
            </div>

            <div className={theme.section}>
                <Plural zero="No buyer" value={data.buyerInfo.users.length} one="Buyer:" other="Buyers:" />
                <ul className={theme.userList}>
                    {data.buyerInfo.users.map((m) => (
                        <li key={`${m.userId}_${m.inviteId}`}>
                            <UserIcon className={theme.avatar} userId={m.userId} sizeInPx={32} />
                            <div className={theme.name}>
                                {m.displayName}
                                <small className={theme.newLine}>
                                    <MailToLink to={m.email} />
                                </small>
                                <small className={theme.newLine}>
                                    <TelLink to={m.phoneNumber} />
                                </small>
                            </div>
                        </li>
                    ))}
                </ul>
                {data.buyerInfo.lastVisitDate && (
                    <Trans>
                        Last active{' '}
                        <DateDistanceDisplay value={data.buyerInfo.lastVisitDate} placement="bottom" disableTooltip />
                    </Trans>
                )}
            </div>
        </div>
    );
};

interface ResidentialUnitTooltipProps {
    children: TooltipChildren;
    residentialUnitId: number;
    delay?: number;
    className?: string;
    linkTab?: ResidentialUnitDetailsTab;
    includeQuestionMark?: boolean;
}

const ResidentialUnitTooltip: React.FC<ResidentialUnitTooltipProps> = ({
    residentialUnitId,
    children,
    className = '',
    delay = 150,
    linkTab,
    includeQuestionMark = true,
}) => {
    const [visible, setVisible] = useState(false);
    const [data, setData] = useState<ResidentialUnitExtraData | null>(null);

    useEffect(() => {
        setData(null);
    }, [residentialUnitId]);

    useEffect(() => {
        async function load() {
            var result = await getResidentialUnitExtraData(residentialUnitId!);
            setData(result);
        }

        if (residentialUnitId && visible && !data) {
            load();
        }
    }, [residentialUnitId, visible, data]);

    let link = `/residential-units/${residentialUnitId}`;

    if (linkTab) {
        link += '/' + linkTab;
    }

    return (
        <Tooltip
            includeQuestionMark={includeQuestionMark}
            color="light"
            className={className}
            delay={delay}
            dynamicWidth
            title={
                data ? (
                    <div className={theme.header}>
                        <span>
                            <Trans>Unit - {data.name}</Trans>&nbsp;
                            {data.residentialUnitGroupName && <>({data.residentialUnitGroupName})</>}
                        </span>
                        <ProjectRelativeLink to={link} className={theme.headerLink}>
                            <Trans>View unit details</Trans>
                        </ProjectRelativeLink>
                    </div>
                ) : undefined
            }
            // this might not be in use. Im not sure :S
            onShow={() => setVisible(true)}
            onHide={() => setVisible(false)}
            content={data ? <Content data={data} /> : <TooltipLoading />}>
            {children}
        </Tooltip>
    );
};

export default ResidentialUnitTooltip;
